import { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { Box } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useMutation, api } from "../../api"
import getIconComponent from "../utils/getIconComponent"
import CustomBackdrop from "../../components/CustomBackdrop"
import { useQueryClient } from "@tanstack/react-query"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"
import ModalImportCSV from "../../components/CustomCSVImportButton"

// Estilos base para el contenedor de la grilla conforme al estándar SIAC
const StyledRootStyles = {
  display: "flex",
  flexDirection: "column",
  marginTop: "64px",
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#196C87" },
  },
})

const Pesajes = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)
  const [openModal, setOpenModal] = useState(false)

  // Mutación para eliminación/anulación de Pesajes
  const { mutateAsync: SaveEliminacionPesaje, isPending: isDeletingPesaje } = useMutation({
    queryKey: ["isDeletingPesaje"],
    fn: async (data) => {
      const response = await api.post("/Pesajes/eliminarPesajes", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["Pesajes"] })
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
            textAlign: "center",
          }}
        >
          <b>Mantenimiento de Pesajes (Cuentas por Pagar)</b>
        </div>

        <CustomBackdrop isLoading={isDeletingPesaje} />

        <Box sx={StyledRootStyles}>
          {/* Modal de Importación */}
          <ModalImportCSV
            open={openModal}
            onClose={() => setOpenModal(false)}
            templateFileName="PlantillaCargaPesajes.csv"
            fieldConfigs={{
              pesoid: { required: true, key: true },
              balcodigo: { required: true },
              procodigo: { required: true },
              artcodigo: { required: true },
              pesocantbruta: { required: true },
              pesocantfinal: { required: true },
            }}
            validateEndpoint="/Pesajes/validarPesajesIMP"
            insertEndpoint="/Pesajes/insertarPesajesIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["Pesajes"] })}
          />

          <CustomConditionalActionsTableServer
            endpoint="/Pesajes/getAllPesajes"
            errorMsgFilterSearch="Error al cargar el listado de pesajes"
            queryKeyModal="Pesajes"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              // Lógica de acciones de fila condicionada por barraAcciones configurada en base de datos
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find((a) => a.acccaption === "EDITAR")
              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (a) => a.acccaption === "ELIMINAR" || a.acccaption === "ANULAR",
              )

              const actions = []
              if (editarAction) {
                actions.push({
                  label: editarAction.acccaption,
                  key: "edit",
                  icon: getIconComponent(editarAction.accnameicono, editarAction.acctipoico),
                  onClick: (row) => navigate("editar", { state: row.original }),
                })
              }
              if (eliminarAction) {
                actions.push({
                  label: eliminarAction.acccaption,
                  key: "delete",
                  icon: getIconComponent(eliminarAction.accnameicono, eliminarAction.acctipoico),
                  onClick: async (row) => {
                    // Validamos visualmente antes de mandar a anular
                    if (row.original.pesoestado === "I" || row.original.pesoestado === "N") {
                      api.showWarning("El pesaje ya se encuentra inactivo/anulado.")
                      return
                    }
                    if (row.original.opid && row.original.opid.trim() !== "") {
                      api.showWarning(
                        `No se puede eliminar. Este pesaje está ligado a la Orden de Pago: ${row.original.opid}`,
                      )
                      return
                    }
                    try {
                      await SaveEliminacionPesaje({ pesoid: row.original.pesoid })
                    } catch (err) {
                      console.error(err)
                    }
                  },
                })
              }
              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              // Lógica de TopToolbar dinámica basada en configuración de base de datos
              const acciones = selectedMenuInfo?.data?.barraAcciones || []
              const toolbarActions = []

              const crearAction = acciones.find((a) => a.acccaption === "CREAR")
              if (crearAction) {
                toolbarActions.push({
                  label: crearAction.acccaption,
                  key: "crearBtn",
                  icon: getIconComponent(crearAction.accnameicono, crearAction.acctipoico),
                  onClick: () => navigate("crear"),
                })
              }

              const exportarAction = acciones.find((a) => a.acccaption === "EXPORTAR")
              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction.acccaption,
                  key: "exportDropdown",
                  icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "pdf",
                      icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                      onClick: ({ columns, data }) => {
                        const title = "Reporte de Pesajes"
                        if (device === "sm") return handleExportDataPdfSMScreen(columns, data, title, title)
                        handleExportDataPdfLGScreen(columns, table.getCoreRowModel().rows, title, title)
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "csv",
                      icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                      onClick: ({ data }) => handleAllExportDataCSV(data, "Pesajes_SIAC"),
                    },
                  ],
                })
              }

              const importarAction = acciones.find((a) => a.acccaption === "IMPORTAR")
              if (importarAction) {
                toolbarActions.push({
                  label: importarAction.acccaption,
                  key: "importBtn",
                  icon: getIconComponent(
                    importarAction.accnameicono || "UploadFile",
                    importarAction.acctipoico || "MaterialIcons",
                  ),
                  onClick: () => setOpenModal(true),
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              { accessorKey: "pesoid", header: "Nº Pesaje", Header: "Nº Pesaje", size: 140 },
              { accessorKey: "pesofecisys", header: "F. Creación", Header: "F. Creación", size: 110 },
              { accessorKey: "pronombre", header: "Proveedor", Header: "Proveedor", size: 220 },
              { accessorKey: "artdescri", header: "Artículo", Header: "Artículo", size: 220 },
              {
                accessorKey: "pesocantbruta",
                header: "Lbs Brutas",
                Header: "Lbs Brutas",
                size: 110,
                Cell: ({ cell }) => <span>{Number(cell.getValue() || 0).toFixed(2)}</span>,
              },
              {
                accessorKey: "pesocantfinal",
                header: "Lbs Netas",
                Header: "Lbs Netas",
                size: 110,
                Cell: ({ cell }) => <span>{Number(cell.getValue() || 0).toFixed(2)}</span>,
              },
              {
                accessorKey: "pesovaltot",
                header: "Total a Pagar",
                Header: "Total a Pagar",
                size: 120,
                Cell: ({ cell }) => <span>${Number(cell.getValue() || 0).toFixed(2)}</span>,
              },
              {
                accessorKey: "opid",
                header: "Orden Pago",
                Header: "Orden Pago",
                size: 130,
                Cell: ({ cell }) => <span>{cell.getValue() || "-"}</span>,
              },
              {
                accessorKey: "pesoestado",
                header: "Estado",
                Header: "Estado",
                size: 90,
                Cell: ({ cell }) => {
                  const status = cell.getValue()
                  if (status === "A") return "ACTIVO"
                  if (status === "I") return "INACTIVO"
                  if (status === "N") return "ANULADO"
                  if (status === "P") return "PAGADO"
                  return status
                },
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default Pesajes
